import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import "./ContactPage.css";
import { fetchContactInfo } from "../utils/contactInfo";

const API_BASE = "https://api.mariamlyluxe.com/public";

// Email icon component
function EmailIcon({ size = 20, color = "#d19e90" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

// Phone icon component
function PhoneIcon({ size = 20, color = "#d19e90" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  );
}

// Clock icon component
function ClockIcon({ size = 20, color = "#d19e90" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12,6 12,12 16,14"/>
    </svg>
  );
}

// Location icon component
function LocationIcon({ size = 20, color = "#d19e90" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

// Instagram icon component
function InstagramIcon({ size = 20, color = "#d19e90" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  );
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    phone: "+961-70 457 945",
    email: "info@mariamlyluxe.com",
    location: "Tripoli",
    instagram: null
  });

  // banner image state
  const [bannerImage, setBannerImage] = useState(null);

  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const info = await fetchContactInfo();
        setContactInfo(info);
      } catch (error) {
        console.error("Failed to load contact info:", error);
      }
    };

    const loadBannerImage = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/pages-banners/contact`, {
          headers: { Accept: "application/json" }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.banner && data.banner.image_url) {
            setBannerImage(data.banner.image_url);
          }
        }
      } catch (e) {
        console.warn("Failed to load banner image:", e);
        // Fallback to default image if API fails
        setBannerImage('../images/crosswalk.jpg');
      }
    };

    loadContactInfo();
    loadBannerImage();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors below");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      toast.success("Message sent successfully! We'll get back to you soon.");
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        message: ""
      });
      setErrors({});
      
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(error.message || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Banner Section */}
      <div 
        className="contact-banner"
        style={{
          backgroundImage: bannerImage ? `url(${bannerImage})` : 'url(../images/crosswalk.jpg)'
        }}
      >
        <div className="contact-banner-content">
          <h1 className="contact-banner-title">Contact Us</h1>
          <div className="contact-breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <span>Contact</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="contact-content">
        <div className="contact-grid">
          {/* Contact Info */}
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <p>
              We'd love to hear from you! Whether you have a question about our products, 
              need help with an order, or just want to say hello, we're here to help.
            </p>
            
            <div className="contact-details">
              <div className="contact-item">
                <EmailIcon />
                <div className="contact-item-content">
                  <h3>Email</h3>
                  <p>{contactInfo.email}</p>
                </div>
              </div>
              
              <div className="contact-item">
                <PhoneIcon />
                <div className="contact-item-content">
                  <h3>Phone</h3>
                  <p>{contactInfo.phone}</p>
                </div>
              </div>
              
              <div className="contact-item">
                <LocationIcon />
                <div className="contact-item-content">
                  <h3>Location</h3>
                  <p>{contactInfo.location}</p>
                </div>
              </div>
              
              {contactInfo.instagram && (
                <div className="contact-item">
                  <InstagramIcon />
                  <div className="contact-item-content">
                    <h3>Instagram</h3>
                    <a 
                      href={`https://www.instagram.com/${contactInfo.instagram.replace('@', '')}/`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#d19e90', textDecoration: 'none' }}
                    >
                      {contactInfo.instagram}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-section">
            <h2>Send us a Message</h2>
            <p>
              Fill out the form below and we'll get back to you as soon as possible.
            </p>
            
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className={`form-group ${errors.name ? 'error' : ''}`}>
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                />
                {errors.name && <div className="error-message">{errors.name}</div>}
              </div>
              
              <div className={`form-group ${errors.email ? 'error' : ''}`}>
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                />
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>
              
              <div className={`form-group ${errors.message ? 'error' : ''}`}>
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us how we can help you..."
                  rows={5}
                />
                {errors.message && <div className="error-message">{errors.message}</div>}
              </div>
              
              <button 
                type="submit" 
                className={`submit-button ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
